import {type FC, useCallback, useEffect, useRef} from 'react';
import {JaaSMeeting} from '@jitsi/react-sdk';
import type {IJitsiMeetExternalApi} from '@jitsi/react-sdk/lib/types';
import CallApi from '../../../apis/callApi.ts';
import {findCurrentScheduleIndex} from "../../../utils/DateUtil.ts";

const APP_ID = import.meta.env.VITE_ICSC_APP_ID;
const APP_NAME = import.meta.env.VITE_ROOM_NAME;

interface ConfSenderProps {
    token: string;
}

const ConfSender: FC<ConfSenderProps> = ({token}) => {
    const apiRef = useRef<IJitsiMeetExternalApi | null>(null);
    const scheduleIdRef = useRef<number | null>(null);
    const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const scheduleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchScheduleId = useCallback(async () => {
        try {
            const {data} = await CallApi().get('/tvs/schedule/client/');
            scheduleIdRef.current = data.slides[0]?.events[findCurrentScheduleIndex(data.slides[0]?.events)].id ?? null;
        } catch (error) {
            console.error('Failed to fetch schedule:', error);
        }
    }, []);

    const syncToBackend = useCallback(async () => {
        if (!apiRef.current || scheduleIdRef.current === null) return;

        const participantCount = apiRef.current.getNumberOfParticipants();
        const payload = {
            schedule_id: scheduleIdRef.current,
            part_count: participantCount,
        };
        try {
            await CallApi().post('/broadcasting/participant/', payload);
        } catch (error) {
            console.error('Failed to sync participants:', error);
        }
    }, []);

    const handleApiReady = useCallback((externalApi: IJitsiMeetExternalApi) => {
        apiRef.current = externalApi;

        fetchScheduleId().then(() => {
            syncToBackend();
        });

        // 1분마다 스케줄 ID 갱신
        scheduleIntervalRef.current = setInterval(fetchScheduleId, 60 * 1000);

        // 10초마다 참가자 수 동기화
        syncIntervalRef.current = setInterval(syncToBackend, 10 * 1000);

        // 참가자 입장/퇴장 시 즉시 동기화
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (externalApi as any).on('participantJoined', syncToBackend);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (externalApi as any).on('participantLeft', syncToBackend);
    }, [fetchScheduleId, syncToBackend]);

    useEffect(() => {
        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
            }
            if (scheduleIntervalRef.current) {
                clearInterval(scheduleIntervalRef.current);
            }
            if (apiRef.current) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (apiRef.current as any).off('participantJoined', syncToBackend);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (apiRef.current as any).off('participantLeft', syncToBackend);
            }
        };
    }, [syncToBackend]);

    const configOverwrite = {
        startWithAudioMuted: false,
        disableLocalVideoFlip: true,
        doNotFlipLocalVideo: true,
        prejoinPageEnabled: false,
        disableAP: true,
        defaultLanguage: 'ko',
    };

    const handleJaaSIFrameRef = (iframeRef: HTMLDivElement) => {
        iframeRef.style.border = '1px solid #3d3d3d';
        iframeRef.style.background = '#3d3d3d';
        iframeRef.style.height = '850px';
    };

    return (
        <JaaSMeeting
            appId={APP_ID}
            roomName={APP_NAME}
            jwt={token}
            configOverwrite={configOverwrite}
            getIFrameRef={handleJaaSIFrameRef}
            onApiReady={handleApiReady}
        />
    );
};

export default ConfSender;
